import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Users, Flame, BellRing, Target, ChevronRight, Loader2, Crown } from "lucide-react";

const STATUS = {
  on_fire: ["On fire", "bg-emerald-100 text-emerald-800"],
  steady: ["Steady", "bg-slate-100 text-slate-700"],
  slipping: ["Slipping", "bg-red-100 text-red-800"],
  at_risk: ["Gone quiet", "bg-amber-100 text-amber-800"],
  quiet: ["Not started", "bg-slate-100 text-slate-500"],
};
const ago = (iso) => {
  if (!iso) return "never";
  const d = Math.floor((Date.now() - new Date(iso)) / 86400000);
  return d === 0 ? "today" : d === 1 ? "yesterday" : `${d}d ago`;
};

export function RepTable({ reps, meId, onOpen, onAssign, onNudged }) {
  const [busy, setBusy] = useState("");
  const nudge = async (r) => {
    setBusy(r.id);
    try { const res = await api.post(`/manager/reps/${r.id}/nudge`); toast.success(`Nudge sent to ${res.data.to}`); onNudged(r.id); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send the nudge"); }
    finally { setBusy(""); }
  };
  return (
    <section className="rounded-3xl border border-slate-200/80 bg-white shadow-[0_4px_20px_-4px_rgba(15,23,42,0.06)] overflow-hidden" data-testid="rep-table">
      <div className="p-5 pb-3 flex items-center justify-between gap-2">
        <h2 className="text-lg font-bold tracking-tight text-slate-900 flex items-center gap-2"><Users className="w-5 h-5 text-emerald-600" /> Your reps</h2>
        <span className="text-xs text-slate-500">Ranked by XP this period · tap a name for scorecards &amp; transcripts</span>
      </div>
      <div className="divide-y divide-slate-100">
        {reps.map((r, i) => {
          const [label, tone] = STATUS[r.status] || STATUS.steady;
          const isMe = r.id === meId;
          return (
            <div key={r.id} className="flex items-center gap-3 px-5 py-3 hover:bg-slate-50/80 transition-colors" data-testid={`rep-row-${r.id}`}>
              <div className="w-7 text-center font-mono text-xs font-bold text-slate-400">{i + 1}</div>
              <button onClick={() => onOpen(r.id)} className="flex items-center gap-3 flex-1 min-w-0 text-left" data-testid={`rep-open-${r.id}`}>
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-100 to-cyan-100 text-emerald-800 font-extrabold flex items-center justify-center shrink-0">{(r.name || "?")[0]}</div>
                <div className="min-w-0">
                  <div className="text-sm font-bold text-slate-900 truncate flex items-center gap-1.5">{r.name}{r.is_manager && <Crown className="w-3.5 h-3.5 text-amber-500" />}<span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${tone}`}>{label}</span></div>
                  <div className="text-xs text-slate-500 truncate">
                    {r.sessions} calls · avg <strong className="text-slate-800">{r.avg_score ?? "—"}</strong>
                    {r.prev_avg != null && r.avg_score != null && <span className={r.avg_score >= r.prev_avg ? "text-emerald-700" : "text-red-700"}> ({r.avg_score >= r.prev_avg ? "+" : ""}{r.avg_score - r.prev_avg})</span>}
                    {" · "}{r.weakest_skill ? <>weakest: <span className="text-amber-800 font-semibold">{r.weakest_skill}</span></> : "no scored calls"}
                    {" · "}last {ago(r.last_practiced_at)}
                  </div>
                </div>
              </button>
              <div className="hidden sm:flex items-center gap-1 text-xs font-bold text-amber-700 w-14 justify-end"><Flame className="w-3.5 h-3.5" />{r.streak}</div>
              <div className="font-mono text-sm font-bold text-emerald-700 w-20 text-right hidden sm:block">+{r.xp} XP</div>
              <div className="flex items-center gap-1 shrink-0">
                {!isMe && (
                  <Button size="sm" variant="outline" className="rounded-full h-8 px-2.5" onClick={() => nudge(r)} disabled={busy === r.id || r.nudged_today} title={r.nudged_today ? "Nudged today" : "Email a nudge"} data-testid={`rep-nudge-${r.id}`}>
                    {busy === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5" />}
                  </Button>
                )}
                <Button size="sm" className="rounded-full h-8 px-2.5 bg-slate-900 hover:bg-slate-800 text-white" onClick={() => onAssign(r)} title="Assign a drill" data-testid={`rep-assign-${r.id}`}><Target className="w-3.5 h-3.5" /></Button>
                <button onClick={() => onOpen(r.id)} className="text-slate-400 hover:text-slate-800 p-1" aria-label="Open"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
